import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ChevronRight } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function Home() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { t } = useLang();
  const [text, setText] = useState("");
  const [recent, setRecent] = useState([]);

  const EXAMPLES = [t("ex.car"), t("ex.job"), t("ex.city"), t("ex.laptop"), t("ex.business")];

  useEffect(() => {
    if (user) {
      api.get("/decisions").then((r) => setRecent(r.data.slice(0, 4))).catch(() => {});
    }
  }, [user]);

  const start = (raw) => {
    if (!raw.trim()) return;
    navigate("/decide", { state: { rawInput: raw.trim() } });
  };

  return (
    <AppShell
      right={
        user ? (
          user.is_pro ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-decide-forestLight px-2.5 py-1 text-[11px] font-bold text-decide-forest" data-testid="pro-badge">
              <Sparkles size={12} /> {t("home.pro")}
            </span>
          ) : (
            <button data-testid="header-upgrade" onClick={() => navigate("/pricing")} className="text-sm font-semibold text-decide-forest">{t("home.upgrade")}</button>
          )
        ) : (
          <button data-testid="header-signin" onClick={login} className="text-sm font-semibold text-decide-ink">{t("home.signin")}</button>
        )
      }
    >
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-decide-ink leading-[1.05]">
          {t("home.title")}
        </h1>
        <p className="mt-4 text-decide-slate text-[15px] leading-relaxed">{t("home.sub")}</p>

        <div className="mt-6 decide-card p-0 overflow-hidden focus-within:border-decide-forest transition-colors">
          <textarea
            data-testid="decision-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("home.placeholder")}
            rows={4}
            className="w-full resize-none bg-transparent px-5 pt-5 pb-2 text-xl font-light text-decide-ink placeholder:text-decide-mute focus:outline-none"
          />
          <div className="flex items-center justify-between px-4 pb-4 pt-1">
            <button data-testid="see-example-btn" onClick={() => start(t("home.demo"))} className="btn-ghost !px-3 text-sm">
              {t("home.seeExample")}
            </button>
            <button data-testid="start-deciding-btn" onClick={() => start(text)} disabled={!text.trim()} className="btn-primary !py-3 !px-5">
              {t("home.startDeciding")} <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              data-testid="example-chip"
              onClick={() => setText(`${t("home.examplePrefix")} ${ex.charAt(0).toLowerCase() + ex.slice(1)}`)}
              className="rounded-full border border-decide-line bg-decide-surface px-3.5 py-2 text-[13px] text-decide-slate transition-colors hover:border-decide-ink hover:text-decide-ink"
            >
              {ex}
            </button>
          ))}
        </div>
      </motion.div>

      {user && recent.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="decide-label">{t("home.recent")}</h2>
            <button onClick={() => navigate("/decisions")} className="text-sm font-medium text-decide-forest">{t("nav.all")}</button>
          </div>
          <div className="mt-3 space-y-2.5">
            {recent.map((d) => (
              <button key={d.id} data-testid="recent-decision-card" onClick={() => navigate(`/decisions/${d.id}`)} className="w-full decide-card !p-4 flex items-center justify-between text-left transition-all hover:border-decide-mute">
                <div className="min-w-0">
                  <p className="font-semibold text-decide-ink truncate">{d.title}</p>
                  <p className="text-[13px] text-decide-slate truncate">{d.recommendation_label || t("home.analysed")}</p>
                </div>
                <ChevronRight size={18} className="text-decide-mute shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
